import { getElementPropertiesFromCategory } from '@shared/helpers/element-category.helpers';
import {
  isElementExpressionProperty,
  isPropertyCountClearable,
} from '@shared/helpers/element-property.helpers';
import {
  createExpression,
  isExpressionValue,
} from '@shared/helpers/expression-factory.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import type { IElementExpressionProperty } from '@shared/models/element-property.model';
import type {
  ExpressionValue,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import {
  type ExpressionInputChangeEvent,
  toExpressionValue,
} from '@features/expressions/ExpressionInput/expression-input-change.helpers';

/**
 * Formula to restore when the user deletes the expression and the property
 * cannot be left without a count. Category rows use their template formula
 * (reinforcement steel is "0"); anything else falls back to a literal 0.
 *
 * @param property - Property whose formula was deleted
 * @param element - Owner, used to resolve conditional category defaults
 */
const categoryDefaultExpression = (
  property: IElementExpressionProperty,
  element: OneOfPropertyElements | undefined,
): ExpressionValue => {
  if (element && isElement(element)) {
    const template = getElementPropertiesFromCategory(element).find(
      (candidate) => candidate.name === property.name,
    );
    if (
      template &&
      isElementExpressionProperty(template) &&
      isExpressionValue(template.count)
    ) {
      return { ...template.count, value: undefined };
    }
  }
  return createExpression({ expression: '0', value: undefined }, 0);
};

/**
 * Maps a classified {@link ExpressionInputChangeEvent} to the property fields
 * that should be updated. Pure function — no side effects.
 *
 * @param event - Classified input change
 * @param property - Property being edited (its count, fallback and inheritance decide clears)
 * @param element - Owner, needed to restore a category default when a formula is deleted
 * @returns Partial {@link IElementExpressionProperty} to merge into the property change
 */
export const resolvePropertyModification = (
  event: ExpressionInputChangeEvent,
  property: IElementExpressionProperty,
  element?: OneOfPropertyElements,
): Partial<IElementExpressionProperty> => {
  const { count } = property;
  switch (event.type) {
    case 'value-change':
    case 'expression-change':
      return { count: toExpressionValue(event.count, count) };

    case 'expression-and-unit-change':
      return { count: toExpressionValue(event.count, count), unit: event.unit };

    case 'unit-change':
      return { unit: event.unit };

    case 'value-clear':
      // Keep the expression formula active, remove only the numeric value override.
      return { count: count ? { ...count, value: undefined } : undefined };

    case 'expression-clear':
      // Clear the entire count so fallbackCount takes over (DEV-3220).
      if (isPropertyCountClearable(property)) return { count: undefined };
      // Deleting the formula must not keep a custom expression. Restore the
      // category default; clearing only the typed number is `value-clear`.
      return { count: categoryDefaultExpression(property, element) };
  }
};
